import React from 'react';
import { Trash2 } from 'lucide-react';
import { ref, remove, get } from 'firebase/database';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { database, auth, storage } from '../../firebase/config';

export default function DeleteBand({ user, bandId, bandData, setError, getBandMembers }) {

  const deleteBand = async () => {
    if (!user || !bandId) return;

    const bandName = bandData?.name || 'this band';
    if (!window.confirm(`Are you sure you want to DELETE ${bandName}? This will remove all songs, gigs, documents, and member access. This action CANNOT be undone.`)) return;
    if (!window.confirm(`Final confirmation: Delete ${bandName} permanently?`)) return;

    try {
      setError('');
      let storageDeleteFailed = false;

      const deleteFileFromStorage = async (storagePath) => {
        if (!storagePath) return;
        try {
          const fileRef = storageRef(storage, storagePath);
          await deleteObject(fileRef);
        } catch (err) {
          if (err.code === 'storage/unauthorized') storageDeleteFailed = true;
        }
      };

      const bandRef = ref(database, `bands/${bandId}`);
      const bandSnapshot = await get(bandRef);

      if (bandSnapshot.exists()) {
        const bandDataComplete = bandSnapshot.val();

        // Delete song files
        if (bandDataComplete.songs) {
          for (const songId of Object.keys(bandDataComplete.songs)) {
            const song = bandDataComplete.songs[songId];
            if (song.lyrics?.storagePath) await deleteFileFromStorage(song.lyrics.storagePath);
            if (song.versions) {
              for (const versionId of Object.keys(song.versions)) {
                if (song.versions[versionId].storagePath) await deleteFileFromStorage(song.versions[versionId].storagePath);
              }
            }
          }
        }

        // Delete gig files
        if (bandDataComplete.gigs) {
          for (const gigId of Object.keys(bandDataComplete.gigs)) {
            const gig = bandDataComplete.gigs[gigId];
            if (gig.setlist?.storagePath) await deleteFileFromStorage(gig.setlist.storagePath);
            if (gig.photos) {
              for (const photoId of Object.keys(gig.photos)) {
                if (gig.photos[photoId].storagePath) await deleteFileFromStorage(gig.photos[photoId].storagePath);
              }
            }
          }
        }

        // Delete band documents
        if (bandDataComplete.bandDocs) {
          const docs = bandDataComplete.bandDocs;
          if (docs.bandPhoto?.storagePath) await deleteFileFromStorage(docs.bandPhoto.storagePath);
          if (docs.bioSmall?.storagePath) await deleteFileFromStorage(docs.bioSmall.storagePath);
          if (docs.bioMedium?.storagePath) await deleteFileFromStorage(docs.bioMedium.storagePath);
          if (docs.bioLarge?.storagePath) await deleteFileFromStorage(docs.bioLarge.storagePath);

          for (const key of ['rider', 'gearList', 'livePhotos']) {
            if (docs[key]) {
              for (const itemId of Object.keys(docs[key])) {
                if (docs[key][itemId].storagePath) await deleteFileFromStorage(docs[key][itemId].storagePath);
              }
            }
          }
        }
      }

      // Remove band link for all members
      const members = getBandMembers();
      for (const member of members) {
        await remove(ref(database, `bandMembers/${member.uid}`));
      }

      // Delete entire band
      await remove(ref(database, `bands/${bandId}`));

      if (storageDeleteFailed) {
        alert('Band deleted from database, but some Storage files remain due to Firebase Storage rules.');
      }

      await signOut(auth);
    } catch (error) {
      console.error('Error deleting band:', error);
      setError('Failed to delete band: ' + error.message);
    }
  };

  return (
    <div className="card-3d rounded-card p-6">
      <h3 className="text-sm font-semibold mb-2 text-text-subtle">Delete Band</h3>
      <p className="text-xs text-text-subtle mb-3">Permanently delete this band and all its data. This cannot be undone.</p>
      <button
        onClick={deleteBand}
        className="bg-transparent border border-red-600 text-red-400 hover:bg-red-600 hover:text-white transition px-6 h-[42px] font-semibold text-sm flex items-center gap-2 rounded-card-btn"
      >
        <Trash2 className="w-4 h-4" />
        Delete Band
      </button>
    </div>
  );
}
